import React, { useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  IconButton,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GroupIcon from '@mui/icons-material/Group';
import LockResetIcon from '@mui/icons-material/LockReset';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';

import axiosInstance from '../config/axiosInstance';
import PageHeader from '../modules/pricingJourney/components/PageHeader';
import ConfirmDialog from '../modules/pricingJourney/components/ConfirmDialog';
import MetricCard from '../modules/pricingJourney/components/MetricCard';
import PasswordField from '../modules/pricingJourney/components/PasswordField';
import { JourneyField, Notice } from '../modules/pricingJourney/components/JourneyBlocks';
import { JourneyButton, JourneyText } from '../modules/pricingJourney/components/JourneyBasics';
import { J } from '../modules/pricingJourney/journeyTokens';
import { EMAIL_RE, focusFirstError, MIN_PASSWORD_LENGTH } from '../modules/pricingJourney/journeyAuth';
import PageSkeleton from '../modules/pricingJourney/components/Skeletons';

// PRC-030: diálogos con el panel del recorrido.
const dialogPaperSx = { bgcolor: J.panel, backgroundImage: 'none', border: `1px solid ${J.border}`, borderRadius: J.radiusCard, p: 3, color: J.text };

interface PricingTeamContext {
  pricing_account_id: string;
  account_name: string | null;
  tier_id: string;
  tier_name: string;
  max_users: number;
  active_members_count: number;
  available_slots: number;
}

interface PricingTeamMember {
  member_id: string;
  pricing_account_id: string;
  user_id: string;
  email: string | null;
  full_name: string | null;
  role: 'admin' | 'member';
  status: 'active' | 'disabled';
  is_account_owner: boolean;
  created_at: string;
  updated_at: string;
}

interface PricingTeamData {
  context: PricingTeamContext;
  members: PricingTeamMember[];
}

interface CreateMemberPayload {
  email: string;
  password: string;
  full_name: string;
}

interface UpdateMemberStatusPayload {
  memberId: string;
  status: 'active' | 'disabled';
}

interface ResetPasswordPayload {
  memberId: string;
  newPassword: string;
}

const fetchPricingTeam = async (): Promise<PricingTeamData> => {
  const [contextResponse, membersResponse] = await Promise.all([
    axiosInstance.get('/pricing-team/context'),
    axiosInstance.get('/pricing-team/members'),
  ]);

  return {
    context: contextResponse.data,
    members: membersResponse.data,
  };
};

const PricingTeamPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { pricingAccess } = useAuth();
  const isTrial = pricingAccess?.state === 'trial';
  const { enqueueSnackbar } = useSnackbar();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [resetMember, setResetMember] = useState<PricingTeamMember | null>(null);
  // PRC-032: desactivar un usuario se confirma antes.
  const [deactivateMember, setDeactivateMember] = useState<PricingTeamMember | null>(null);
  // PRC-030: errores por campo, validados al salir de cada uno y al enviar.
  const [createErrors, setCreateErrors] = useState<Partial<Record<'fullName' | 'email' | 'password' | 'confirm', string>>>({});
  const [resetErrors, setResetErrors] = useState<Partial<Record<'password' | 'confirm', string>>>({});

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [temporaryPassword, setTemporaryPassword] = useState('');
  const [confirmTemporaryPassword, setConfirmTemporaryPassword] = useState('');

  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');

  const {
    data,
    isLoading,
    error,
  } = useQuery<PricingTeamData, Error>({
    queryKey: ['pricing-team'],
    queryFn: fetchPricingTeam,
  });

  const invalidatePricingTeam = async () => {
    await queryClient.invalidateQueries({
      queryKey: ['pricing-team'],
    });
  };

  const resetCreateForm = () => {
    setFullName('');
    setEmail('');
    setTemporaryPassword('');
    setConfirmTemporaryPassword('');
  };

  const closeCreateDialog = () => {
    if (createMemberMutation.isPending) {
      return;
    }

    setCreateDialogOpen(false);
    resetCreateForm();
    setCreateErrors({});
  };

  const closeResetDialog = () => {
    if (resetPasswordMutation.isPending) {
      return;
    }

    setResetMember(null);
    setNewPassword('');
    setConfirmNewPassword('');
    setResetErrors({});
  };

  const createFieldError = (key: 'fullName' | 'email' | 'password' | 'confirm'): string | undefined => {
    if (key === 'fullName') return fullName.trim() ? undefined : 'Escribe el nombre del usuario.';
    if (key === 'email') return !email.trim() ? 'Escribe su email.' : EMAIL_RE.test(email.trim()) ? undefined : 'Este email no parece correcto.';
    if (key === 'password') return temporaryPassword.length >= MIN_PASSWORD_LENGTH ? undefined : `La contraseña necesita al menos ${MIN_PASSWORD_LENGTH} caracteres.`;
    return confirmTemporaryPassword && confirmTemporaryPassword === temporaryPassword ? undefined : 'Las contraseñas no coinciden.';
  };

  const resetFieldError = (key: 'password' | 'confirm'): string | undefined => {
    if (key === 'password') return newPassword.length >= MIN_PASSWORD_LENGTH ? undefined : `La contraseña necesita al menos ${MIN_PASSWORD_LENGTH} caracteres.`;
    return confirmNewPassword && confirmNewPassword === newPassword ? undefined : 'Las contraseñas no coinciden.';
  };

  const createMemberMutation = useMutation({
    mutationFn: async (payload: CreateMemberPayload) => {
      const { data: createdMember } = await axiosInstance.post(
        '/pricing-team/members',
        payload,
      );

      return createdMember;
    },
    onSuccess: async () => {
      enqueueSnackbar('Usuario creado correctamente.', {
        variant: 'success',
      });

      setCreateDialogOpen(false);
      resetCreateForm();
      await invalidatePricingTeam();
    },
    onError: (err: any) => {
      enqueueSnackbar(
        err?.response?.data?.detail || 'No se pudo crear el usuario.',
        {
          variant: 'error',
        },
      );
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({
      memberId,
      status,
    }: UpdateMemberStatusPayload) => {
      const { data: updatedMember } = await axiosInstance.patch(
        `/pricing-team/members/${memberId}/status`,
        {
          status,
        },
      );

      return updatedMember;
    },
    onSuccess: async (_data, variables) => {
      enqueueSnackbar(
        variables.status === 'active'
          ? 'Usuario reactivado correctamente.'
          : 'Usuario desactivado correctamente.',
        {
          variant: 'success',
        },
      );

      await invalidatePricingTeam();
    },
    onError: (err: any) => {
      enqueueSnackbar(
        err?.response?.data?.detail || 'No se pudo actualizar el usuario.',
        {
          variant: 'error',
        },
      );
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: async ({
      memberId,
      newPassword,
    }: ResetPasswordPayload) => {
      const { data: updatedMember } = await axiosInstance.post(
        `/pricing-team/members/${memberId}/reset-password`,
        {
          new_password: newPassword,
        },
      );

      return updatedMember;
    },
    onSuccess: () => {
      enqueueSnackbar('Contraseña temporal actualizada.', {
        variant: 'success',
      });

      setResetMember(null);
      setNewPassword('');
      setConfirmNewPassword('');
    },
    onError: (err: any) => {
      enqueueSnackbar(
        err?.response?.data?.detail ||
          'No se pudo actualizar la contraseña.',
        {
          variant: 'error',
        },
      );
    },
  });

  const canCreateMember = useMemo(() => {
    return (
      fullName.trim().length > 0 &&
      email.trim().length > 0 &&
      temporaryPassword.length >= 8 &&
      temporaryPassword === confirmTemporaryPassword
    );
  }, [
    fullName,
    email,
    temporaryPassword,
    confirmTemporaryPassword,
  ]);

  const canResetPassword = useMemo(() => {
    return (
      newPassword.length >= 8 &&
      newPassword === confirmNewPassword
    );
  }, [
    newPassword,
    confirmNewPassword,
  ]);

  const handleCreateMember = (event?: React.FormEvent) => {
    event?.preventDefault();
    const errors = {
      fullName: createFieldError('fullName'),
      email: createFieldError('email'),
      password: createFieldError('password'),
      confirm: createFieldError('confirm'),
    };
    setCreateErrors(errors);
    if (!canCreateMember || Object.values(errors).some(Boolean)) {
      focusFirstError([[errors.fullName, 'team-name'], [errors.email, 'team-email'], [errors.password, 'team-pass'], [errors.confirm, 'team-pass2']]);
      return;
    }

    createMemberMutation.mutate({
      full_name: fullName.trim(),
      email: email.trim(),
      password: temporaryPassword,
    });
  };

  const handleResetPassword = (event?: React.FormEvent) => {
    event?.preventDefault();
    const errors = { password: resetFieldError('password'), confirm: resetFieldError('confirm') };
    setResetErrors(errors);
    if (!resetMember || !canResetPassword || errors.password || errors.confirm) {
      focusFirstError([[errors.password, 'team-reset-pass'], [errors.confirm, 'team-reset-pass2']]);
      return;
    }

    resetPasswordMutation.mutate({
      memberId: resetMember.member_id,
      newPassword,
    });
  };

  // PRC-031: estructura al instante en lugar de una rueda.
  if (isLoading) {
    return <PageSkeleton eyebrow="Mi cuenta" title="Mi equipo" variant="cards" />;
  }

  if (error || !data) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Alert severity="error">
          No se pudo cargar la gestión de usuarios de Pricing.
        </Alert>
      </Container>
    );
  }

  const {
    context,
    members,
  } = data;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#0f172a',
        py: 3,
      }}
    >
      <Container maxWidth="lg">
        {/* PRC-032: cabecera común. */}
        <PageHeader
          eyebrow="Mi cuenta"
          title="Mi equipo"
          description="Usuarios que trabajan sobre los mismos datos de Pricing."
          back="origin"
          actions={(
              <Button
                variant="contained"
                startIcon={<PersonAddAlt1Icon />}
                onClick={() => setCreateDialogOpen(true)}
                disabled={context.available_slots <= 0}
                sx={{
                  bgcolor: '#38bdf8',
                  color: '#0f172a',
                  fontWeight: 800,
                  px: 3,
                  py: 1.5,
                  '&:hover': {
                    bgcolor: '#7dd3fc',
                  },
                }}
              >
                Añadir usuario
              </Button>
          )}
        />

        {/* PRC-032: tarjetas de indicador comunes. */}
        <Stack
          direction={{
            xs: 'column',
            md: 'row',
          }}
          spacing={2}
          sx={{
            mb: 4,
          }}
        >
          <MetricCard label="Plan actual" value={context.tier_name} />
          <MetricCard label="Usuarios activos" value={`${context.active_members_count} de ${context.max_users}`} />
          <MetricCard
            label="Plazas disponibles"
            value={context.available_slots}
            valueColor={context.available_slots > 0 ? '#86efac' : '#fca5a5'}
          />
        </Stack>

        {context.available_slots <= 0 && (
          // PRC-020: en la prueba la cuenta es de un usuario; se explica en lugar de dar error.
          isTrial ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              Durante la prueba la cuenta es de un solo usuario. Invitar a compañeros se activa al contratar un plan.{' '}
              <Link component={RouterLink} to="/subscription">Ver planes</Link>
            </Alert>
          ) : (
          <Alert
            severity="warning"
            sx={{
              mb: 3,
            }}
          >
            Has alcanzado el límite de usuarios incluido en tu plan.
          </Alert>
          )
        )}

        <TableContainer
          component={Paper}
          sx={{
            bgcolor: alpha('#1e293b', 0.72),
            border: '1px solid rgba(255,255,255,0.08)',
            overflowX: 'auto',
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: '#94a3b8' }}>
                  Usuario
                </TableCell>

                <TableCell sx={{ color: '#94a3b8' }}>
                  Rol
                </TableCell>

                <TableCell sx={{ color: '#94a3b8' }}>
                  Estado
                </TableCell>

                <TableCell
                  align="right"
                  sx={{ color: '#94a3b8' }}
                >
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {members.map((member) => (
                <TableRow
                  key={member.member_id}
                  sx={{
                    '& td': {
                      borderColor: 'rgba(255,255,255,0.06)',
                    },
                  }}
                >
                  <TableCell>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar
                        sx={{
                          width: 36,
                          height: 36,
                          bgcolor: alpha('#38bdf8', 0.12),
                          color: '#38bdf8',
                          fontWeight: 800,
                        }}
                      >
                        {(member.full_name || member.email || '?')
                          .charAt(0)
                          .toUpperCase()}
                      </Avatar>

                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#f8fafc',
                            fontWeight: 700,
                          }}
                        >
                          {member.full_name || 'Usuario sin nombre'}
                        </Typography>

                        <Typography
                          variant="caption"
                          sx={{
                            color: '#94a3b8',
                          }}
                        >
                          {member.email || 'Email no disponible'}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>

                  <TableCell>
                    <Chip
                      size="small"
                      icon={
                        member.role === 'admin'
                          ? <AdminPanelSettingsIcon />
                          : <GroupIcon />
                      }
                      label={
                        member.role === 'admin'
                          ? 'Administrador'
                          : 'Usuario'
                      }
                      sx={{
                        bgcolor: member.role === 'admin'
                          ? alpha('#fcd34d', 0.12)
                          : alpha('#38bdf8', 0.12),
                        color: member.role === 'admin'
                          ? '#fcd34d'
                          : '#7dd3fc',
                      }}
                    />
                  </TableCell>

                  <TableCell>
                    <Chip
                      size="small"
                      label={
                        member.status === 'active'
                          ? 'Activo'
                          : 'Desactivado'
                      }
                      sx={{
                        bgcolor: member.status === 'active'
                          ? alpha('#22c55e', 0.12)
                          : alpha('#ef4444', 0.12),
                        color: member.status === 'active'
                          ? '#86efac'
                          : '#fca5a5',
                      }}
                    />
                  </TableCell>

                  <TableCell align="right">
                    {!member.is_account_owner && (
                      <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="flex-end"
                      >
                        <Tooltip
                          title={
                            member.status === 'active'
                              ? 'Desactivar usuario'
                              : 'Reactivar usuario'
                          }
                        >
                          <IconButton
                            size="small"
                            disabled={updateStatusMutation.isPending}
                            onClick={() => {
                              if (member.status === 'active') {
                                setDeactivateMember(member);
                                return;
                              }
                              updateStatusMutation.mutate({
                                memberId: member.member_id,
                                status: 'active',
                              });
                            }}
                            sx={{
                              color: member.status === 'active'
                                ? '#fca5a5'
                                : '#86efac',
                            }}
                          >
                            {member.status === 'active'
                              ? <BlockIcon fontSize="small" />
                              : <CheckCircleIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Cambiar contraseña">
                          <IconButton
                            size="small"
                            onClick={() => setResetMember(member)}
                            sx={{
                              color: '#7dd3fc',
                            }}
                          >
                            <LockResetIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>

      <ConfirmDialog
        open={!!deactivateMember}
        title="¿Desactivar este usuario?"
        text={`${deactivateMember?.full_name || deactivateMember?.email || 'El usuario'} dejará de poder entrar en la cuenta. Puedes reactivarlo cuando quieras.`}
        confirmLabel="Desactivar"
        busyLabel="Desactivando…"
        busy={updateStatusMutation.isPending}
        onCancel={() => setDeactivateMember(null)}
        onConfirm={() => {
          if (!deactivateMember) return;
          updateStatusMutation.mutate(
            { memberId: deactivateMember.member_id, status: 'disabled' },
            { onSettled: () => setDeactivateMember(null) },
          );
        }}
      />

      {/* PRC-030: formularios con las piezas del recorrido (ojo, Bloq Mayús, requisitos, Enter envía). */}
      <Dialog
        open={createDialogOpen}
        onClose={closeCreateDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <Box component="form" noValidate onSubmit={handleCreateMember} sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <JourneyText variant="h2" component="h2" sx={{ m: 0 }}>Añadir usuario</JourneyText>
          <Notice tone="info">
            Trabajará sobre los mismos datos de Pricing. Comunícale la contraseña temporal de forma segura.
          </Notice>
          <JourneyField
            id="team-name" label="Nombre y apellidos" value={fullName} autoComplete="off" autoFocus
            error={createErrors.fullName}
            onChange={(event) => { setFullName(event.target.value); setCreateErrors((x) => ({ ...x, fullName: undefined })); }}
            onBlur={() => { if (fullName) setCreateErrors((x) => ({ ...x, fullName: createFieldError('fullName') })); }}
          />
          <JourneyField
            id="team-email" label="Email" type="email" value={email} autoComplete="off"
            error={createErrors.email}
            onChange={(event) => { setEmail(event.target.value); setCreateErrors((x) => ({ ...x, email: undefined })); }}
            onBlur={() => { if (email) setCreateErrors((x) => ({ ...x, email: createFieldError('email') })); }}
          />
          <PasswordField
            id="team-pass" label="Contraseña temporal" value={temporaryPassword} autoComplete="new-password" showRequirements
            error={createErrors.password}
            onChange={(event) => { setTemporaryPassword(event.target.value); setCreateErrors((x) => ({ ...x, password: undefined })); }}
            onBlur={() => { if (temporaryPassword) setCreateErrors((x) => ({ ...x, password: createFieldError('password') })); }}
          />
          <PasswordField
            id="team-pass2" label="Repite la contraseña temporal" value={confirmTemporaryPassword} autoComplete="new-password"
            error={createErrors.confirm}
            onChange={(event) => { setConfirmTemporaryPassword(event.target.value); setCreateErrors((x) => ({ ...x, confirm: undefined })); }}
            onBlur={() => { if (confirmTemporaryPassword) setCreateErrors((x) => ({ ...x, confirm: createFieldError('confirm') })); }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', flexWrap: 'wrap', mt: 0.5 }}>
            <JourneyButton kind="ghost" onClick={closeCreateDialog} disabled={createMemberMutation.isPending}>Cancelar</JourneyButton>
            <JourneyButton type="submit" disabled={createMemberMutation.isPending}>
              {createMemberMutation.isPending ? 'Creando usuario…' : 'Crear usuario'}
            </JourneyButton>
          </Box>
        </Box>
      </Dialog>

      <Dialog
        open={Boolean(resetMember)}
        onClose={closeResetDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <Box component="form" noValidate onSubmit={handleResetPassword} sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <JourneyText variant="h2" component="h2" sx={{ m: 0 }}>Cambiar contraseña temporal</JourneyText>
          <Notice tone="warn">
            Cambiarás la contraseña de {resetMember?.email || 'este usuario'}.
          </Notice>
          <PasswordField
            id="team-reset-pass" label="Nueva contraseña" value={newPassword} autoComplete="new-password" autoFocus showRequirements
            error={resetErrors.password}
            onChange={(event) => { setNewPassword(event.target.value); setResetErrors((x) => ({ ...x, password: undefined })); }}
            onBlur={() => { if (newPassword) setResetErrors((x) => ({ ...x, password: resetFieldError('password') })); }}
          />
          <PasswordField
            id="team-reset-pass2" label="Repite la nueva contraseña" value={confirmNewPassword} autoComplete="new-password"
            error={resetErrors.confirm}
            onChange={(event) => { setConfirmNewPassword(event.target.value); setResetErrors((x) => ({ ...x, confirm: undefined })); }}
            onBlur={() => { if (confirmNewPassword) setResetErrors((x) => ({ ...x, confirm: resetFieldError('confirm') })); }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', flexWrap: 'wrap', mt: 0.5 }}>
            <JourneyButton kind="ghost" onClick={closeResetDialog} disabled={resetPasswordMutation.isPending}>Cancelar</JourneyButton>
            <JourneyButton type="submit" disabled={resetPasswordMutation.isPending}>
              {resetPasswordMutation.isPending ? 'Guardando…' : 'Cambiar contraseña'}
            </JourneyButton>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default PricingTeamPage;